import { createRootRouteWithContext, Outlet } from "@tanstack/react-router"
import { AlertTriangle, ArrowUp, RefreshCw, WifiOff } from "lucide-react"
import { useEffect, useState } from "react"

import type { AppRouterContext } from "@/lib/router-context"

import { Button } from "@/components/button"
import { AppDevTools } from "@/components/LazyDevTools"
import { Toaster } from "@/components/ui/toast"

import "../index.css"

const RootLayout = () => {
  return (
    <div className="app-shell min-h-screen bg-background text-foreground">
      <OfflineIndicatorBanner />
      <a
        href="#main-content"
        className="sr-only top-3 left-3 z-50 rounded-md bg-card px-3 py-2 text-sm font-medium text-foreground shadow-sm focus:not-sr-only"
      >
        Skip to main content
      </a>

      <Outlet />
      <BackToTopButton />

      <Toaster />
      <AppDevTools />
    </div>
  )
}

const SCROLL_THRESHOLD_PX = 360

function BackToTopButton() {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (typeof window === "undefined") {
      return
    }

    const onScroll = () => {
      setIsVisible(window.scrollY > SCROLL_THRESHOLD_PX)
    }

    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })

    return () => {
      window.removeEventListener("scroll", onScroll)
    }
  }, [])

  if (!isVisible) {
    return null
  }

  return (
    <Button
      type="button"
      size="icon"
      variant="outline"
      className="fixed right-4 bottom-4 z-40 rounded-full border-border/80 bg-card/90 shadow-lg backdrop-blur"
      onClick={() => {
        window.scrollTo({ top: 0, behavior: "smooth" })
      }}
      aria-label="Back to top"
    >
      <ArrowUp className="size-4" />
    </Button>
  )
}

function OfflineIndicatorBanner() {
  const [isOffline, setIsOffline] = useState(false)

  useEffect(() => {
    if (typeof window === "undefined") {
      return
    }
    const controller = new AbortController()

    const syncNetworkState = () => {
      setIsOffline(!window.navigator.onLine)
    }

    syncNetworkState()
    window.addEventListener("online", syncNetworkState, {
      signal: controller.signal,
    })
    window.addEventListener("offline", syncNetworkState, {
      signal: controller.signal,
    })

    return () => {
      controller.abort()
      // window.removeEventListener("online", syncNetworkState)
      // window.removeEventListener("offline", syncNetworkState)
    }
  }, [])

  if (!isOffline) {
    return null
  }

  return (
    <div
      className="fixed inset-x-3 top-3 z-50 flex justify-center px-1"
      role="status"
      aria-live="polite"
    >
      <div className="inline-flex items-center gap-2 rounded-full border border-warning/30 bg-warning/12 px-4 py-2 text-sm font-medium text-warning-foreground shadow-md backdrop-blur">
        <WifiOff className="size-4" aria-hidden="true" />
        <span>
          You are offline. Changes may not sync until your connection returns.
        </span>
      </div>
    </div>
  )
}

function RootErrorBoundary({ reset }: { reset?: () => void }) {
  return (
    <div
      className="app-shell min-h-screen bg-background px-4 py-10 text-foreground md:px-6 lg:px-8"
      role="alert"
    >
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-5 rounded-3xl border border-border/70 bg-card/85 p-6 shadow-xl backdrop-blur md:p-8">
        <div className="text-kicker inline-flex w-fit items-center gap-2 rounded-full border border-danger/30 bg-danger/10 px-3 py-1 text-xs font-semibold tracking-[0.14em] text-danger">
          <AlertTriangle className="size-3.5" aria-hidden="true" />
          Unexpected issue
        </div>
        <div className="space-y-2">
          <h1 className="text-3xl font-semibold md:text-4xl">
            Something went wrong
          </h1>
          <p className="text-sm wrap-break-word text-muted-foreground md:text-base">
            We hit an unexpected error while loading this page. Try again or
            refresh to recover.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button type="button" onClick={() => reset?.()}>
            Try again
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              window.location.reload()
            }}
          >
            <RefreshCw className="mr-2 size-4" aria-hidden="true" />
            Reload page
          </Button>
        </div>
      </div>
    </div>
  )
}

export const Route = createRootRouteWithContext<AppRouterContext>()({
  component: RootLayout,
  errorComponent: RootErrorBoundary,
})
